import * as angular from 'angular';
import * as _ from 'lodash';
import { IStoreScope } from 'src/app/sdc/flux/sdc-store.interface';
import { WizardUtils } from 'src/app/shared/services/wizard-utils/wizard-utils.service';

import { UtilitiesService } from 'src/ng2/shared/services/utilities/utilities.service';
import { NextRegentsAdminDate } from '../../../../../../../ng2/shared/constants/next-regents-admin-date.constant';
import { RegentsExamPrepStatuses } from '../../../../../../../ng2/shared/constants/regents-exam-prep-statuses.constant';
import { RegentsExam } from '../../../../../../../ng2/shared/constants/regents.constant';
// tslint:disable-next-line:max-line-length
import { StoreListenerUtils } from '../../../../../../shared/services/store-listener-utils/store-listener-utils.service';

interface IExamValue {
  readonly key: string;
  readonly nextAdminDates: string[];
  readonly humanName: string;
  readonly shortName: string;
}

export class RegentsExamsScheduledTableCtrl {
  static $inject: string[] = [
    '$scope',
    'StudentSupportModal',
    'Rollbar',
    'WizardUtils',
    'UtilitiesService',
    'StoreListenerUtils',
  ];

  // public by default. Accessible to template
  tableCols: any[];
  tableRows: any;
  tableColTotals: any;
  studentSupportAction;

  private _offeredExams: any;
  private _allScheduledExamsByStatus: any;
  private currentSchool: any;
  private nvNextRegentsAdminDate: any;
  private nvRegentsExamPrepStatuses: any;
  private nvRegentsExam: any;

  constructor(
    public $scope: IStoreScope,
    private nvStudentSupportModal,
    private Rollbar,
    private wizardUtils: WizardUtils,
    private utils: UtilitiesService,
    private storeListenerUtils: StoreListenerUtils,
  ) {}

  $onInit(): void {
    this.storeListenerUtils.startListeningAndLoad(this, [
      ['studentSupports'],
      ['schoolSupports'],
      ['students'],
      ['filteredFlattenedStudents'],
    ]);
  }

  setUi(): void {
    // the regents exam dates are not in a known ISO format.
    // ordinarily moment would be able to handle this by falling back to using js
    // Date to parse it.However, the fallback method is being deprecated.
    // Moment recommends explicitly defining a format for the full string.
    // http://momentjs.com/guides/#/warnings/js-date/
    this.studentSupportAction = this.generateAdditionalWizardFilterAction();
    this.nvNextRegentsAdminDate = NextRegentsAdminDate;
    this.nvRegentsExamPrepStatuses = RegentsExamPrepStatuses;
    this.nvRegentsExam = RegentsExam;
    this._offeredExams = _.filter(this.nvRegentsExam, (examValue: any) => examValue.isOffered);

    this.tableRows = {
      rowNoPrep: {},
      rowFailing: {},
      rowPassing: {},
      rowAdditionalPrep: {},
      rowCourseAdditionalPrep: {},
    };

    // Table headers
    this.tableCols = _.map(this._offeredExams, examValue => {
      const col: any = angular.copy(examValue);
      const date = col.nextAdminDates[0];
      // figure out if the offered exam is actually offered this regents admin!
      let monthDay = 'Not Offered';
      let amPm;
      if (date) {
        if (date === 'Schedule Unknown') {
          monthDay = '';
          amPm = 'Offered';
        } else {
          const parsedDate = this.utils.parseDate(date);
          monthDay = parsedDate.format('M/DD');
          amPm = parsedDate.format('A');
        }
      }
      col.monthDay = monthDay;
      col.amPm = amPm;
      return col;
    });
    // Sorted Table headers
    this.tableCols = _.sortBy(this.tableCols, ['monthDay', 'amPm', 'order']);

    this.fetchStudents();
  }

  private populateTableRows(student, examKey, prepStatusForExam) {
    const { studentId, _id } = student;
    switch (prepStatusForExam) {
      case this.nvRegentsExamPrepStatuses.NO_PREP:
        this.tableRows.rowNoPrep[examKey].students.push(studentId);
        this.tableRows.rowNoPrep[examKey].studentsToPatch.push(_id);
        break;
      case this.nvRegentsExamPrepStatuses.COURSE_FAILING:
        this.tableRows.rowFailing[examKey].students.push(studentId);
        this.tableRows.rowFailing[examKey].studentsToPatch.push(_id);
        break;
      case this.nvRegentsExamPrepStatuses.COURSE_PASSING:
        this.tableRows.rowPassing[examKey].students.push(studentId);
        this.tableRows.rowPassing[examKey].studentsToPatch.push(_id);
        break;
      case this.nvRegentsExamPrepStatuses.ADD_PREP:
        this.tableRows.rowAdditionalPrep[examKey].students.push(studentId);
        this.tableRows.rowAdditionalPrep[examKey].studentsToPatch.push(_id);
        break;
      case this.nvRegentsExamPrepStatuses.COURSE_ADD_PREP:
        this.tableRows.rowCourseAdditionalPrep[examKey].students.push(studentId);
        this.tableRows.rowCourseAdditionalPrep[examKey].studentsToPatch.push(_id);
        break;
      default:
        break;
    }
    return this.tableRows;
  }

  private createTableRows(examValue: IExamValue) {
    return _.each(this.tableRows, row => {
      row[examValue.key] = {};
      row[examValue.key].students = [];
      row[examValue.key].studentsToPatch = [];
      row[examValue.key].examValue = examValue;
      row[examValue.key].options = {
        columnKeys: [
          'cohort',
          examValue.key + 'PlannedNextAdmin',
          examValue.key + 'PrepStatus',
          examValue.key + 'AlignedCourses',
          examValue.key + 'AlignedSupports',
        ],
        examKey: examValue,
      };
    });
  }

  private fetchStudents() {
    const flattened = this.wizardUtils.getFilteredFlattenedStudents();
    this.tableColTotals = {};
    _.each(this.tableCols, (examValue: IExamValue) => {
      const filteredStudents = _.filter(flattened, student => {
        if (!_.isUndefined(student[`${examValue.key}PrepStatus`])) return student;
      });

      this.createTableRows(examValue);
      _.each(filteredStudents, filteredStudent => {
        const prepStatusForExam = filteredStudent[`${examValue.key}PrepStatus`];
        if (prepStatusForExam) this.populateTableRows(filteredStudent, examValue.key, prepStatusForExam);
      });

      // get total scheduled for each exam across all prep statuses
      this._allScheduledExamsByStatus = _.map(this.tableRows, row => row[examValue.key].students.length);
      const colTotal: number = _.sum(this._allScheduledExamsByStatus);
      this.tableColTotals[examValue.key] = colTotal;
    });
  }

  private generateAdditionalWizardFilterAction() {
    return {
      assignRegents: {
        updateCount: null,
        open: (studentIds, options): void => {
          const _ids = this.wizardUtils.getIdsFromDistrictIds(studentIds);
          const promise = this.nvStudentSupportModal.show(this.currentSchool._id, _ids, null, options.examKey.key);
          promise.catch(err => this.Rollbar.error(`Unable to update studentSupports`, err));
        },
        label: `Assign Student Support`,
      },
    };
  }
}
